import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'sonner';
import { Header } from '@/components/Header';
import { StatusBadge } from '@/components/StatusBadge';
import { ArrowLeft, MapPin, Package, Calendar, Clock3, User, Mail, Phone, Loader2, Save, Brain, AlertTriangle, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const ESTADOS = ['Notificada', 'Asignada', 'En Ruta', 'Recogida', 'Completada', 'Cancelada'];

export default function AdminDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [solicitud, setSolicitud] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editEstado, setEditEstado] = useState('');
  const [editNotas, setEditNotas] = useState('');
  const [editFechaReal, setEditFechaReal] = useState('');
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/admin/solicitudes/${id}`, { headers: authHeaders() })
      .then(r => {
        setSolicitud(r.data);
        setEditEstado(r.data.estado);
        setEditNotas(r.data.notas_admin || '');
        setEditFechaReal(r.data.fecha_recogida_real || '');
      })
      .catch(() => { toast.error('Error al cargar solicitud'); navigate('/admin/solicitudes'); })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!solicitud || !mapRef.current || mapInstanceRef.current) return;
    if (!solicitud.latitude || !solicitud.longitude) return;
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    });
    const map = L.map(mapRef.current, {
      center: [solicitud.latitude, solicitud.longitude],
      zoom: 16,
      zoomControl: true,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap', maxZoom: 19,
    }).addTo(map);
    L.marker([solicitud.latitude, solicitud.longitude]).addTo(map)
      .bindPopup(solicitud.ticket_code).openPopup();
    mapInstanceRef.current = map;
    return () => { map.remove(); mapInstanceRef.current = null; };
  }, [solicitud]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await axios.patch(`${API}/admin/solicitudes/${id}`, {
        estado: editEstado,
        notas_admin: editNotas,
        fecha_recogida_real: editFechaReal || null,
      }, { headers: authHeaders() });
      setSolicitud(resp.data);
      toast.success('Solicitud actualizada correctamente');
    } catch (err) {
      toast.error('Error al guardar cambios');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F9FC]">
        <Header adminMode />
        <div className="flex justify-center py-20">
          <Loader2 size={32} className="animate-spin text-[#009940]" />
        </div>
      </div>
    );
  }

  if (!solicitud) return null;

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <Header adminMode />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-5">
          <Link to="/admin/solicitudes" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#374B67] transition-colors">
            <ArrowLeft size={15} /> Solicitudes
          </Link>
          <span className="text-slate-300">/</span>
          <span className="text-sm font-mono font-medium text-[#374B67]">{solicitud.ticket_code}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5">
          {/* Left: Details */}
          <div className="space-y-4">
            {/* Header card */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">Solicitud</p>
                  <h1 className="ticket-code text-2xl text-[#374B67]">{solicitud.ticket_code}</h1>
                  <p className="text-sm text-slate-500 mt-1">{solicitud.fecha_peticion} {solicitud.hora_peticion} · Recibida vía {solicitud.recibida_por}</p>
                </div>
                <StatusBadge status={solicitud.estado} size="lg" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: MapPin, label: 'Zona', value: solicitud.zona },
                  { icon: Package, label: 'Tipo', value: solicitud.tipo_enseres },
                  { icon: Calendar, label: 'Fecha preferida', value: solicitud.fecha_preferida },
                  { icon: Clock3, label: 'Fecha recogida real', value: solicitud.fecha_recogida_real || '—' },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Icon size={13} className="text-slate-400" />
                      <span className="text-xs text-slate-500 font-medium">{label}</span>
                    </div>
                    <p className="text-sm font-medium text-slate-900">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Contacto */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h2 className="font-semibold text-[#374B67] mb-3">Datos de contacto</h2>
              <div className="space-y-2">
                {[
                  { icon: User, label: 'Nombre', value: solicitud.nombre_completo || 'No indicado' },
                  { icon: Mail, label: 'Email', value: solicitud.email || 'No indicado' },
                  { icon: Phone, label: 'Teléfono', value: solicitud.telefono || 'No indicado' },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-center gap-3 py-2 border-b border-slate-100">
                    <Icon size={14} className="text-slate-400" />
                    <span className="text-sm text-slate-500 w-20">{label}</span>
                    <span className="text-sm font-medium text-slate-900">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Descripción */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h2 className="font-semibold text-[#374B67] mb-3">Descripción de los enseres</h2>
              <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-xl p-4">{solicitud.detalles}</p>
            </div>

            {/* AI Suggestions */}
            {(solicitud.ai_categoria || solicitud.ai_prioridad) && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Brain size={16} className="text-[#004D5A]" />
                  <h2 className="font-semibold text-[#004D5A]">Sugerencias automáticas</h2>
                  <span className="text-xs text-slate-400 font-normal">· Revisa antes de aplicar cambios</span>
                </div>
                <div className="border-l-4 border-[#004D5A] bg-teal-50 rounded-r-xl p-4 space-y-2">
                  {solicitud.ai_categoria && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-teal-700 font-semibold w-24">Categoría IA</span>
                      <span className="text-sm font-medium text-teal-900">{solicitud.ai_categoria}</span>
                    </div>
                  )}
                  {solicitud.ai_prioridad && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-teal-700 font-semibold w-24">Prioridad IA</span>
                      <span className="text-sm font-medium text-teal-900">{solicitud.ai_prioridad}</span>
                    </div>
                  )}
                  {solicitud.ai_volumen && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-teal-700 font-semibold w-24">Volumen est.</span>
                      <span className="text-sm font-medium text-teal-900">{solicitud.ai_volumen}</span>
                    </div>
                  )}
                  {solicitud.ai_peligroso && (
                    <div className="flex items-center gap-1.5 text-amber-700">
                      <AlertTriangle size={13} />
                      <span className="text-xs font-semibold">Puede contener materiales peligrosos</span>
                    </div>
                  )}
                  {solicitud.ai_etiquetas?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {solicitud.ai_etiquetas.map(t => (
                        <span key={t} className="bg-teal-100 text-teal-800 text-xs px-2.5 py-0.5 rounded-full">{t}</span>
                      ))}
                    </div>
                  )}
                  {solicitud.ai_notas && (
                    <p className="text-xs text-teal-700 mt-2 italic">{solicitud.ai_notas}</p>
                  )}
                </div>
              </div>
            )}

            {/* Audit trail */}
            {solicitud.audit_trail?.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h2 className="font-semibold text-[#374B67] mb-3">Historial de cambios</h2>
                <div className="space-y-3">
                  {[...solicitud.audit_trail].reverse().map((entry, idx) => (
                    <div key={idx} className="timeline-item">
                      <div className="timeline-dot bg-[#69C7EA]/20">
                        <Check size={10} className="text-[#374B67]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{entry.accion}</p>
                        {entry.detalles && <p className="text-xs text-slate-500">{entry.detalles}</p>}
                        <p className="text-xs text-slate-400 mt-0.5">{entry.timestamp?.replace('T', ' ').substring(0, 16)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Actions + Map */}
          <div className="space-y-4">
            {/* Update actions */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h2 className="font-semibold text-[#374B67] mb-4">Actualizar solicitud</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Estado</label>
                  <div className="grid grid-cols-2 gap-2">
                    {ESTADOS.map(e => (
                      <button
                        key={e}
                        onClick={() => setEditEstado(e)}
                        className={`py-2 px-3 rounded-xl border text-sm font-medium transition-colors ${
                          editEstado === e
                            ? 'bg-[#374B67] border-[#374B67] text-white'
                            : 'border-slate-200 text-slate-700 hover:border-[#374B67]/30'
                        }`}
                        data-testid={`admin-estado-option-${e.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Fecha recogida real</label>
                  <input
                    type="date"
                    value={editFechaReal}
                    onChange={e => setEditFechaReal(e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#69C7EA]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notas internas</label>
                  <textarea
                    value={editNotas}
                    onChange={e => setEditNotas(e.target.value)}
                    placeholder="Notas para el equipo operativo..."
                    rows={3}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#69C7EA] resize-none"
                    data-testid="admin-notas-textarea"
                  />
                </div>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 bg-[#009940] hover:bg-[#007F35] text-white py-3 rounded-xl font-semibold text-sm transition-colors disabled:opacity-60"
                  data-testid="admin-save-button"
                >
                  {saving ? <><Loader2 size={15} className="animate-spin" /> Guardando...</> : <><Save size={15} /> Guardar cambios</>}
                </button>
              </div>
            </div>

            {/* Map */}
            {solicitud.latitude && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                  <MapPin size={14} className="text-[#69C7EA]" />
                  <h2 className="font-semibold text-[#374B67] text-sm">Ubicación de recogida</h2>
                </div>
                <div ref={mapRef} style={{ height: '220px' }} />
                <div className="px-4 py-2.5 bg-slate-50 text-xs text-slate-500">
                  {solicitud.latitude?.toFixed(5)}, {solicitud.longitude?.toFixed(5)}
                </div>
              </div>
            )}

            {/* Image */}
            {solicitud.imagen_url && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                <h2 className="font-semibold text-[#374B67] text-sm mb-3">Imagen adjunta</h2>
                <img
                  src={`${BACKEND_URL}${solicitud.imagen_url}`}
                  alt="Enseres"
                  className="w-full rounded-xl object-cover max-h-48"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
